import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, Sparkles, Video as VideoIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { VideoCard } from "@/components/VideoCard";
import { FestiveHero } from "@/components/FestiveHero";
import { EventsShowcase } from "@/components/EventsShowcase";
import { AboutSection } from "@/components/AboutSection";
import { FeaturedCarousel } from "@/components/FeaturedCarousel";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const LIMIT = 12;

export default function Catalogue() {
  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [counts, setCounts] = useState({});
  const [grandTotal, setGrandTotal] = useState(0);
  const [rawCategories, setRawCategories] = useState([]);
  const [whatsapp, setWhatsapp] = useState("919630024060");
  const [price, setPrice] = useState(290);
  const [currency, setCurrency] = useState("₹");
  const [featured, setFeatured] = useState([]);
  const [activeCat, setActiveCat] = useState("All Events");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const categories = ["All Events", ...rawCategories];

  const loadCounts = useCallback(() => {
    api.get("/categories/counts").then(({ data }) => {
      setCounts(data.counts || {});
      setGrandTotal(data.total || 0);
    });
  }, []);

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setWhatsapp(data.whatsapp_number);
      setRawCategories(data.categories);
      setPrice(data.price);
      setCurrency(data.currency);
    });
    api.get("/videos/featured").then(({ data }) => setFeatured(data));
    loadCounts();
  }, [loadCounts]);

  const filterParams = useCallback(() => {
    const p = {};
    if (activeCat !== "All Events") p.category = activeCat;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [activeCat, search]);

  useEffect(() => {
    setLoading(true);
    let ignore = false;
    const t = setTimeout(async () => {
      const params = { ...filterParams(), skip: 0, limit: LIMIT };
      const [list, cnt] = await Promise.all([
        api.get("/videos", { params }),
        api.get("/videos/count", { params: filterParams() }),
      ]);
      if (ignore) return;
      setVideos(list.data);
      setSkip(list.data.length);
      setTotal(cnt.data.total);
      setLoading(false);
    }, 250);
    return () => { ignore = true; clearTimeout(t); };
  }, [filterParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    const params = { ...filterParams(), skip, limit: LIMIT };
    const { data } = await api.get("/videos", { params });
    setVideos((v) => [...v, ...data]);
    setSkip((s) => s + data.length);
    setLoadingMore(false);
  };

  const scrollToCatalogue = () => {
    document.getElementById("catalogue")?.scrollIntoView({ behavior: "smooth" });
  };

  const pickEvent = (cat) => {
    setSearch("");
    setActiveCat(cat);
    setTimeout(scrollToCatalogue, 80);
  };

  const hasMore = videos.length < total;

  return (
    <div className="App">
      <Header whatsappNumber={whatsapp} />

      <FestiveHero
        whatsapp={whatsapp}
        total={grandTotal}
        categoriesCount={rawCategories.length}
        onExplore={scrollToCatalogue}
      />

      <EventsShowcase categories={rawCategories} counts={counts} onPick={pickEvent} />

      <AboutSection />

      <FeaturedCarousel videos={featured} whatsapp={whatsapp} price={price} currency={currency} />

      {/* Catalogue */}
      <section id="catalogue" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {/* Search */}
        <div className="mt-2 max-w-xl mx-auto relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-amber-600" />
          <Input
            data-testid="search-videos-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or event keyword (e.g. Royal Wedding)..."
            className="h-14 pl-12 pr-4 rounded-2xl bg-white border-amber-500/30 focus-visible:ring-amber-500/40 text-base shadow-sm"
          />
        </div>

        {/* Category pills */}
        <div className="mt-6 flex gap-2.5 overflow-x-auto sm:overflow-visible sm:flex-wrap no-scrollbar pb-2 justify-start sm:justify-center">
          {categories.map((cat) => {
            const active = activeCat === cat;
            const count = cat === "All Events" ? grandTotal : counts[cat] || 0;
            return (
              <button
                key={cat}
                data-testid={`category-pill-${cat}`}
                onClick={() => setActiveCat(cat)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
                  active
                    ? "bg-amber-950 text-amber-50 border-amber-950 shadow-md"
                    : "bg-white text-amber-900 border-amber-300/60 hover:border-amber-500 hover:bg-amber-50"
                }`}
              >
                {cat}
                <span className={`ml-1.5 text-[11px] ${active ? "text-amber-300" : "text-amber-500"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="mt-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-amber-500/20 bg-white overflow-hidden">
                  <div className="aspect-video bg-amber-100/50 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 w-20 bg-amber-100 rounded-full animate-pulse" />
                    <div className="h-6 w-3/4 bg-amber-100/70 rounded animate-pulse" />
                    <div className="h-12 bg-emerald-100/60 rounded-xl animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div data-testid="no-videos-message" className="text-center py-20">
              <VideoIcon className="h-12 w-12 text-amber-400 mx-auto" />
              <p className="mt-4 font-serif text-xl text-amber-900">No invites found</p>
              <p className="text-stone-500 mt-1">Try a different category or search term.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                {videos.map((v, i) => (
                  <VideoCard key={v.id} video={v} whatsappNumber={whatsapp} index={i} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-12 flex justify-center">
                  <Button
                    data-testid="load-more-btn"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="h-12 px-8 bg-amber-950 hover:bg-amber-900 text-amber-50 font-semibold rounded-xl"
                  >
                    {loadingMore ? <Loader2 className="h-5 w-5 animate-spin" /> : `Load More (${total - videos.length} left)`}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <footer className="border-t border-amber-500/20 bg-[#120E0C] text-amber-100/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <span className="font-serif text-lg text-amber-100">Soulful Invites</span>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} Soulful Invites · Crafted with love.</p>
        </div>
      </footer>
    </div>
  );
}
